import { Typography, TypographyProps } from '@mui/material';
import type { Market as MarketType, OutcomeProperties, PublishedEventParticipants } from '../../@types';
import useLocalization from '../../hooks/useLocalization';
import { formatSpecialValue } from '../../utils/specialValueFormatters';
import { SpecialValuesDisplayProps } from '../../types/specialValues';
import { getAccordionTitle } from '../organisms/market-renderers/OMMarketRenderer';
import { getMatchResultTotalsOutcomeName } from '../../utils/ftbMarketHelpers';
import { isMatchResultTotalsMarket } from '../../utils/marketGrouping';
import { MARKET_DISPLAY_TYPE, MarketDisplayType } from '../../types/enums';

import { mergeSx } from '../../utils/styles';

const SpecialValues = ({
  outcomeName,
  sportId,
  specialValues,
  marketTypeName,
  specialValueModel,
  wrapInParenthesis = true,
  numericOnly = false,
  ...rest
}: SpecialValuesDisplayProps) => {
  const { t } = useLocalization();

  const formattedDisplayValue = formatSpecialValue({
    marketTypeName,
    outcomeName,
    sportId,
    specialValues,
    numericOnly,
    wrapInParenthesis,
    t,
    specialValueModel,
  });

  return (
    <Typography component='span' variant='inherit' noWrap {...rest}>
      {formattedDisplayValue}
    </Typography>
  );
};

type OutcomeProps = TypographyProps & {
  outcomeName: string;
  outcomeShortName?: string;
  numericOnly?: boolean;
  marketName?: string;
  marketTypeName?: string;
  marketDisplayType?: MarketDisplayType;
  participants?: PublishedEventParticipants;
  eventName?: string;
} & OutcomeProperties;

const Outcome = ({
  outcomeName,
  outcomeShortName,
  numericOnly,
  specialValue,
  playerName,
  marketName,
  marketTypeName,
  marketDisplayType,
  participants,
  eventName,
  ...rest
}: OutcomeProps) => {
  const { getLocalizedOutcomeName } = useLocalization();

  const getDisplayName = () => {
    // Display only SV for FTB market renderer
    if (numericOnly && specialValue) {
      return specialValue;
    }

    if (outcomeShortName && specialValue) {
      // Map shortName and SV for FTB markets on betslip
      return `${playerName ?? ''} ${outcomeShortName} (${specialValue})`;
    }

    if (marketDisplayType === MARKET_DISPLAY_TYPE.GROUPED && isMatchResultTotalsMarket(marketTypeName)) {
      const formatted = getMatchResultTotalsOutcomeName(outcomeName, marketName ?? '', participants, eventName);

      if (formatted) {
        return formatted;
      }
    }

    return outcomeShortName || getLocalizedOutcomeName(outcomeName);
  };

  const displayName = getDisplayName().trim();

  return (
    <Typography
      variant='inherit'
      component='span'
      {...rest}
      id='outcome-name'
      sx={mergeSx(
        {
          textAlign: 'center',
        },
        ...(Array.isArray(rest.sx) ? rest.sx : [rest.sx])
      )}
    >
      {displayName}
    </Typography>
  );
};

type MarketProps = TypographyProps & {
  market?: MarketType;
  marketName?: string;
  marketType?: string;
  marketShortName?: string;
};

const Market = ({ market, marketName, marketType, marketShortName, ...rest }: MarketProps) => {
  const { getLocalizedMarketName } = useLocalization();
  const { marketType: marketTypeData, shortName: marketShort } = market ?? {};
  const shortName = marketShortName ?? marketShort;

  if (shortName) {
    return (
      <Typography variant='body2' component='span' {...rest}>
        {shortName}
      </Typography>
    );
  }

  const { name: marketTypeName } = marketTypeData ?? {};

  const marketDisplayName = market ? getAccordionTitle(market) : (marketName ?? '');
  const localizedName = getLocalizedMarketName(marketDisplayName, marketTypeName ?? marketType);

  return (
    <Typography variant='body2' component='span' {...rest}>
      {localizedName}
    </Typography>
  );
};

export const BetDetails = {
  Outcome,
  SpecialValues,
  Market,
};
